<script setup lang="ts" generic="T extends FilterParamsBase">
import { BaseButtonKindEnum } from "@/components/Base/Button";
import {
  FilterButtonsProps,
  FilterCategory,
  FilterParamsBase,
} from "@/types/filter.types";
import { ref } from "vue";

type FilterOffCanvasProps = {
  categories: FilterCategory[];
  currentParams: T;
  open: boolean;
  hasQuery: boolean;
  defaultParams?: string[];
};

const props = defineProps<FilterOffCanvasProps>();

const { t } = useI18n();

const emit = defineEmits(["cancel", "finished", "reset", "update:modelValue"]);

const handleFilterChange = (label: string, value: T[typeof label]) => {
  emit("update:modelValue", { label, value });
};

const handleReset = () => {
  emit("reset");
  resetAccordions();
};

const categoryHasOptions = (category: FilterCategory) =>
  category.props.options.some((option) => !!option.value);

const ACCORDION_ID = "filter-accordion";

const accordionRefs = ref<Array<Element | ComponentPublicInstance>>([]);

const accordionStates = ref(
  props.categories.reduce(
    (acc, category, index) => {
      acc[category.id] = index === 0;
      return acc;
    },
    {} as Record<string, boolean>
  )
);

const resetAccordions = () => {
  accordionStates.value = props.categories.reduce(
    (acc, _, index) => {
      acc[props.categories[index].id] = index === 0;
      return acc;
    },
    {} as Record<string, boolean>
  );
};

const handleAccordionUpdate = (categoryId: string, expanded: boolean) => {
  accordionStates.value[categoryId] = expanded;
};

const handleButtonGroupKeyDown = (
  event: KeyboardEvent,
  categoryIndex: number
) => {
  if (
    event.key === "ArrowUp" &&
    (event.target as HTMLElement).classList.contains("filter-button__input")
  ) {
    // If we're on the first button and press ArrowUp, focus on the category header
    const firstButton = event.target as HTMLElement;
    if (firstButton === firstButton.parentElement?.firstElementChild) {
      event.preventDefault();
      (accordionRefs.value[categoryIndex] as HTMLElement)?.focus();
    }
  }
};

const getSelectedCount = (category: FilterCategory) => {
  if (props.defaultParams?.includes(category.id)) return;
  const params = (props.currentParams as Record<string, string[]>)[category.id];
  if (!params || params.length === 0) {
    return 0;
  }
  return params.length;
};

const getSelectedValue = (category: FilterCategory) => {
  const outsideValue = Array.isArray(props.currentParams[category.id])
    ? (props.currentParams[category.id] as string[])[0]
    : props.currentParams[category.id];

  const option = category.props.options.find(
    (option) => option.value === outsideValue
  );

  return option?.label;
};
</script>

<template>
  <teleport to="body">
    <BaseOffCanvas class="filter-off-canvas" :open="open">
      <header class="offcanvas-header filter-off-canvas__header">
        <strong class="filter-off-canvas__title">
          {{ t("global.filterContent") }}
        </strong>
        <button
          type="button"
          data-bs-dismiss="offcanvas"
          class="btn-close"
          aria-label="Close"
        ></button>
      </header>
      <div class="offcanvas-body p-0">
        <div :id="ACCORDION_ID" class="accordion">
          <template v-for="(category, index) in categories">
            <BaseAccordion
              v-if="categoryHasOptions(category)"
              :key="category.id"
              :name="category.id"
              :parent-id="ACCORDION_ID"
              :expanded="accordionStates[category.id]"
              @update:expanded="
                (expanded: boolean) =>
                  handleAccordionUpdate(category.id, expanded)
              "
            >
              <template #title>
                <div class="d-flex align-items-center position-relative">
                  <span>{{ category.props.label }}</span>

                  <BaseNotificationBadge
                    v-if="getSelectedCount(category)"
                    :count="getSelectedCount(category)"
                    size="lg"
                    class="ms-2"
                  />
                  <span
                    v-if="defaultParams?.includes(category.id)"
                    class="fw-semibold ms-1"
                  >
                    {{ getSelectedValue(category) }}
                  </span>
                </div>
              </template>
              <FilterButtons
                v-bind="category.props as FilterButtonsProps"
                :selected-values="
                  (currentParams as Record<string, string[]>)[category.id]
                "
                @change="handleFilterChange(category.id, $event)"
                @keydown="
                  (event: KeyboardEvent) =>
                    handleButtonGroupKeyDown(event, index)
                "
              />
            </BaseAccordion>
          </template>
        </div>
      </div>
      <div class="offcanvas-footer justify-content-start">
        <IconButton
          :kind="BaseButtonKindEnum.LINK"
          type="button"
          icon-name="arrow-counterclockwise"
          class="fw-semibold px-0"
          icon-position="left"
          :disabled="!hasQuery"
          @click="handleReset"
        >
          {{ t("global.resetFilter") }}
        </IconButton>
      </div>
    </BaseOffCanvas>
  </teleport>
</template>

<style scoped lang="scss">
.filter-off-canvas {
  &__header {
    position: relative;
  }

  &__title {
    white-space: nowrap;
    pointer-events: none;
    font-weight: $font-weight-semibold;
  }

  .accordion-item {
    padding-top: 1px;
    border: none;
    border-bottom: 1px solid $border-color;
  }
}
</style>
